import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { ZoomIn, ZoomOut, RotateCcw, Move, Mouse } from 'lucide-react';
import { prepareInlineSvgMarkup } from '../utils/inlineSvg';
import {
  removeAlternatePinNameMarkup,
  renderAlternatePinNameMarkup,
} from '../utils/alternatePinCalloutSvg';
import { applyDefaultPublicPinoutCalloutColors } from '../utils/publicPinoutCalloutColors';
import {
  getPinAlternateNameEntries,
} from '../utils/pinAlternateNames';
import {
  getPinFilterGroups,
  normalizePinFilterGroupList,
} from '../utils/pinFilterGroups';

const ViewerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
`;

const ViewerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 8px 8px 0 0;
  min-height: 54px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    min-height: 46px;
  }
`;

const HeaderStatusGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
`;

const ViewerContent = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  border-radius: 0 0 8px 8px;
`;

const SVGContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isPanning',
})`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: ${props => props.isPanning ? 'grabbing' : 'grab'};
  user-select: none;
  touch-action: none; /* Prevent default touch behaviors */
  
  svg {
    display: block;
    width: 100% !important;
    height: auto !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain;
    pointer-events: all; /* Ensure SVG captures mouse events */
    margin: 0 auto;
  }
`;

const SVGMarkup = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 12px;
  background: ${props => props.$active ? '#0f172a' : 'white'};
  color: ${props => props.$active ? 'white' : '#475569'};
  border: 1px solid ${props => props.$active ? '#0f172a' : '#e2e8f0'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$active ? '#020617' : '#f1f5f9'};
    border-color: ${props => props.$active ? '#020617' : '#cbd5e1'};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    min-height: 36px;
    padding: 0 10px;
    font-size: 11px;
  }
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: white;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 18px;
    height: 18px;
    color: currentColor;
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const ZoomLevel = styled.div`
  background: rgba(255, 255, 255, 0.9);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  border: 1px solid #e2e8f0;
  backdrop-filter: blur(4px);
`;

const PanIndicator = styled.div`
  background: rgba(59, 130, 246, 0.9);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(4px);
  display: ${props => props.show ? 'block' : 'none'};
`;

const PIN_CLICK_TARGET_SELECTOR = [
  '.pin-rect',
  '.pin-hole',
  '.pin-callout-line',
  '.pin-callout-line-hit',
  '.pin-callout-bubble',
  '.pin-callout-text',
  '.pin-callout-anchor',
  '.pin-callout-anchor-hit',
  '.micropinouts-alt-pin-callout-bubble',
  '.micropinouts-alt-pin-callout-text',
].join(', ');

const SVGViewer = ({ 
  svgContent, 
  onPinClick, 
  className,
  pins = [],
  headerLeftContent = null,
  initialZoom = 1,
  minZoom = 0.1,
  maxZoom = 5,
  displayScale = 1,
  enablePan = true,
  enableZoom = true,
  enableWheelZoom = true,
  showWheelZoomToggle = false,
  showAlternatePinCalloutColors = true,
  activeFilterGroups = [],
}) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [zoom, setZoom] = useState(initialZoom);
  const [wheelZoomEnabled, setWheelZoomEnabled] = useState(enableWheelZoom);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const [showPanIndicator, setShowPanIndicator] = useState(false);
  const [panModeEnabled, setPanModeEnabled] = useState(false);
  const [showAlternatePinNames, setShowAlternatePinNames] = useState(true);
  const normalizedDisplayScale = useMemo(() => {
    const parsedScale = Number(displayScale);
    if (!Number.isFinite(parsedScale)) {
      return 1;
    }

    return Math.min(3, Math.max(0.25, parsedScale));
  }, [displayScale]);
  const alternatePinNamesSource = Array.isArray(pins) ? pins : [];
  const normalizedActiveFilterGroups = useMemo(
    () => normalizePinFilterGroupList(activeFilterGroups),
    [activeFilterGroups]
  );
  const alternatePinNamesRenderSignature = JSON.stringify(
    alternatePinNamesSource.map((pin) => ({
      pinId: pin?.id ?? pin?.pin_id ?? pin?.pinId ?? pin?.pin_number ?? pin?.pinNumber ?? pin?.pin_name ?? pin?.pinName ?? '',
      filterGroups: getPinFilterGroups(pin),
      alternatePinNames: pin?.alternate_pin_names ?? pin?.alternatePinNames ?? '',
      alternateNameEntries: getPinAlternateNameEntries(pin),
    }))
  );
  const activeFilterGroupsRenderSignature = normalizedActiveFilterGroups.join('|');
  const pinsWithAlternateNames = alternatePinNamesSource
    .map((pin) => ({
      pin,
      alternateNameEntries: getPinAlternateNameEntries(pin),
    }))
    .filter(({ alternateNameEntries }) => alternateNameEntries.length > 0);
  const hasAlternatePinNames = pinsWithAlternateNames.length > 0;
  const actualZoom = zoom * normalizedDisplayScale;
  const inlineSvgMarkup = useMemo(() => prepareInlineSvgMarkup(svgContent), [svgContent]);

  // Keep board-fit compensation separate from the user-facing zoom percentage.
  useEffect(() => {
    if (svgRef.current) {
      const svg = svgRef.current.querySelector('svg');
      if (svg) {
        svg.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${actualZoom})`;
        svg.style.transformOrigin = 'center center';
      }
    }
  }, [actualZoom, pan]);

  useEffect(() => {
    setWheelZoomEnabled(enableWheelZoom);
  }, [enableWheelZoom]);

  useEffect(() => {
    setShowAlternatePinNames(hasAlternatePinNames);
  }, [hasAlternatePinNames, inlineSvgMarkup]);

  // Handle zoom in
  const handleZoomIn = () => {
    if (enableZoom && zoom < maxZoom) {
      setZoom(prev => Math.min(prev * 1.2, maxZoom));
    }
  };

  // Handle zoom out
  const handleZoomOut = () => {
    if (enableZoom && zoom > minZoom) {
      setZoom(prev => Math.max(prev / 1.2, minZoom));
    }
  };

  // Handle reset view
  const handleReset = () => {
    setZoom(Math.max(minZoom, Math.min(maxZoom, initialZoom || 1)));
    setPan({ x: 0, y: 0 });
  };

  // Handle pan mode toggle
  const handlePanModeToggle = () => {
    setPanModeEnabled(!panModeEnabled);
    if (isPanning) {
      setIsPanning(false);
      setShowPanIndicator(false);
    }
  };

  const handleWheelZoomToggle = () => {
    if (!enableZoom) return;
    setWheelZoomEnabled(prev => !prev);
  };

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e) => {
    if (!enableZoom || !wheelZoomEnabled) return;
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(minZoom, Math.min(maxZoom, zoom * delta));
    
    // Only prevent default if zoom actually changes (not at limits)
    if (newZoom !== zoom) {
      e.preventDefault();
      e.stopPropagation();
      setZoom(newZoom);
    }
  }, [enableZoom, maxZoom, minZoom, wheelZoomEnabled, zoom]);

  // Handle pan start
  const handleMouseDown = (e) => {
    if (!enablePan || !panModeEnabled) return;
    
    setIsPanning(true);
    setLastPanPoint({ x: e.clientX, y: e.clientY });
    setShowPanIndicator(true);
  };

  // Handle pan move
  const handleMouseMove = useCallback((e) => {
    if (!isPanning || !enablePan) return;
    
    const deltaX = e.clientX - lastPanPoint.x;
    const deltaY = e.clientY - lastPanPoint.y;
    
    setPan(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));
    
    setLastPanPoint({ x: e.clientX, y: e.clientY });
  }, [enablePan, isPanning, lastPanPoint]);

  // Handle pan end
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setShowPanIndicator(false);
  }, []);

  // Handle touch events for mobile
  const handleTouchStart = (e) => {
    if (!enablePan || !panModeEnabled || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    setIsPanning(true);
    setLastPanPoint({ x: touch.clientX, y: touch.clientY });
    setShowPanIndicator(true);
  };

  const handleTouchMove = useCallback((e) => {
    if (!isPanning || !enablePan || e.touches.length !== 1) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    const deltaX = touch.clientX - lastPanPoint.x;
    const deltaY = touch.clientY - lastPanPoint.y;
    
    setPan(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));
    
    setLastPanPoint({ x: touch.clientX, y: touch.clientY });
  }, [enablePan, isPanning, lastPanPoint]);

  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
    setShowPanIndicator(false);
  }, []);

  // Add global event listeners for pan
  useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isPanning, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Add wheel event listener to container with passive: false to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !wheelZoomEnabled || !enableZoom) return;

    const handleWheelWithOptions = (e) => {
      handleWheel(e);
    };

    // Add event listener with passive: false to ensure preventDefault works
    container.addEventListener('wheel', handleWheelWithOptions, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheelWithOptions);
    };
  }, [wheelZoomEnabled, enableZoom, handleWheel]);

  useEffect(() => {
    const root = svgRef.current;
    if (!root) return;

    // Try to find the <svg> element (it may not be there on first render)
    let cancelled = false;
    let svgEl = root.querySelector('svg');

    const attach = () => {
      if (!svgEl || cancelled) return;
      const handler = (e) => {
        // TEMP: sanity log
        // console.log('[SVG click]', e.target); 

        if (isPanning) return;
        const t = e.target;
        if (!t || !(t instanceof Element)) return;
        const el = t.closest(PIN_CLICK_TARGET_SELECTOR);
        if (!el) return;
        const calloutGroup = el.closest('[data-callout-key][data-micropinouts-callout="1"]');

        const payload = {
          pin: el.dataset.pin || calloutGroup?.getAttribute('data-pin') || '',
          group: el.dataset.group || calloutGroup?.getAttribute('data-group') || '',
          svgId: el.dataset.svgid || el.id || calloutGroup?.getAttribute('data-svgid') || '',
          pinNumber: el.dataset.pinNumber || calloutGroup?.getAttribute('data-pin-number') || '',
          calloutKey: el.dataset.calloutKey || calloutGroup?.getAttribute('data-callout-key') || '',
          calloutType: el.dataset.calloutType || calloutGroup?.getAttribute('data-callout-type') || '',
          customId: el.dataset.customId || calloutGroup?.getAttribute('data-custom-id') || '',
          source: el.classList && el.classList.contains('pin-hole') ? 'hole' : 'rect',
        };

        if (typeof onPinClick === 'function') onPinClick(payload, e);
      };

      svgEl.addEventListener('click', handler);
      // store cleanup on the element so we remove the *same* handler
      svgEl.__pinHandler = handler;
    };

    // If not present yet, observe until it appears
    if (!svgEl) {
      const obs = new MutationObserver(() => {
        svgEl = root.querySelector('svg');
        if (svgEl) {
          attach();
          obs.disconnect();
        }
      });
      obs.observe(root, { childList: true, subtree: true });
      return () => { cancelled = true; obs.disconnect(); };
    } else {
      attach();
      return () => {
        if (svgEl && svgEl.__pinHandler) {
          svgEl.removeEventListener('click', svgEl.__pinHandler);
          delete svgEl.__pinHandler;
        }
      };
    }
  }, [inlineSvgMarkup, isPanning, onPinClick]); // include normalized markup so it rebinds after content changes

  useEffect(() => {
    setZoom(Math.max(minZoom, Math.min(maxZoom, initialZoom || 1)));
    setPan({ x: 0, y: 0 });
  }, [inlineSvgMarkup, initialZoom, minZoom, maxZoom]);

  useEffect(() => {
    const wrapper = svgRef.current;
    if (!wrapper) return;

    let observer = null;
    const svg = wrapper.querySelector('svg');

    if (svg) {
      applyDefaultPublicPinoutCalloutColors(svg, pins);
      renderAlternatePinNameMarkup(svg, {
        pins,
        showAlternatePinNames,
        showAlternatePinCalloutColors,
        activeFilterGroups,
      });
    } else {
      observer = new MutationObserver(() => {
        const nextSvg = wrapper.querySelector('svg');
        if (!nextSvg) {
          return;
        }

        applyDefaultPublicPinoutCalloutColors(nextSvg, pins);
        renderAlternatePinNameMarkup(nextSvg, {
          pins,
          showAlternatePinNames,
          showAlternatePinCalloutColors,
          activeFilterGroups,
        });
        observer.disconnect();
      });

      observer.observe(wrapper, { childList: true, subtree: true });
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }

      const currentSvg = wrapper.querySelector('svg');
      if (currentSvg) {
        removeAlternatePinNameMarkup(currentSvg);
      }
    };
  }, [
    activeFilterGroups,
    activeFilterGroupsRenderSignature,
    alternatePinNamesRenderSignature,
    inlineSvgMarkup,
    pins,
    showAlternatePinCalloutColors,
    showAlternatePinNames,
  ]);

  return (
    <ViewerContainer className={className}>
      <ViewerHeader>
        <HeaderStatusGroup>
          <ZoomLevel>
            {Math.round(zoom * 100)}%
          </ZoomLevel>
          {headerLeftContent}
          <PanIndicator show={showPanIndicator && panModeEnabled}>
            Pan Mode Active
          </PanIndicator>
        </HeaderStatusGroup>
        
        <ControlsContainer>
          {hasAlternatePinNames && (
            <ToggleButton
              type="button"
              onClick={() => setShowAlternatePinNames((prev) => !prev)}
              title={showAlternatePinNames ? 'Hide Alternate Pin Names' : 'Show Alternate Pin Names'}
              aria-label={showAlternatePinNames ? 'Hide Alternate Pin Names' : 'Show Alternate Pin Names'}
              aria-pressed={showAlternatePinNames}
              $active={showAlternatePinNames}
            >
              Alt Names
            </ToggleButton>
          )}
          {enableZoom && (
            <>
              <ControlButton
                onClick={handleZoomIn}
                disabled={zoom >= maxZoom}
                title="Zoom In"
              >
                <ZoomIn />
              </ControlButton>
              <ControlButton
                onClick={handleZoomOut}
                disabled={zoom <= minZoom}
                title="Zoom Out"
              >
                <ZoomOut />
              </ControlButton>
              {showWheelZoomToggle && (
                <ControlButton
                  onClick={handleWheelZoomToggle}
                  title={wheelZoomEnabled ? 'Disable Scroll Zoom' : 'Enable Scroll Zoom'}
                  aria-label={wheelZoomEnabled ? 'Disable Scroll Zoom' : 'Enable Scroll Zoom'}
                  aria-pressed={wheelZoomEnabled}
                  style={{
                    background: wheelZoomEnabled ? '#3b82f6' : 'white',
                    color: wheelZoomEnabled ? 'white' : '#475569'
                  }}
                >
                  <Mouse />
                </ControlButton>
              )}
            </>
          )}
          <ControlButton
            onClick={handleReset}
            title="Reset View"
          >
            <RotateCcw />
          </ControlButton>
          {enablePan && (
            <ControlButton
              onClick={handlePanModeToggle}
              title={panModeEnabled ? "Disable Pan Mode" : "Enable Pan Mode (Click to toggle, then drag to pan)"}
              style={{
                background: panModeEnabled ? '#3b82f6' : 'white',
                color: panModeEnabled ? 'white' : '#475569'
              }}
            >
              <Move />
            </ControlButton>
          )}
        </ControlsContainer>
      </ViewerHeader>

      <ViewerContent>
        <SVGContainer
          ref={containerRef}
          isPanning={isPanning}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          //onClick={handleClick}
        >
          <SVGMarkup
            ref={svgRef}
            id="svgContainerInner"
            data-svg-artwork-root="1"
            dangerouslySetInnerHTML={{
              __html: inlineSvgMarkup || '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #64748b;">No SVG content available</div>'
            }}
          />
        </SVGContainer>
      </ViewerContent>
    </ViewerContainer>
  );
};

export default SVGViewer;
